/* =====================================================================
 * AppRouter — 전체 라우트 트리 (공개 + 인증 라우트)
 *
 * Phase 11-3d: 라우트 트리의 골격 (Suspense + Routes + AuthGuard + 공개
 * 라우트 + 인증 라우트 + `/` 루트 리다이렉트 + 404 폴백) 은 모두
 * `@auw/workspace-app` 의 `WorkspaceAppRouter` 로 이관됐다. 이 파일은
 * 이제 B2C 전용 lazy import / SEO wrapper / `routeConfig.ROUTE_ENTRIES`
 * 를 shared 가 기대하는 route-first 셰이프로 맵핑해서 넘기는 shim 이다.
 *
 * 새 모드 추가 시 여전히 `routeConfig.ts` 한 군데에만 추가하면
 * 자동으로 등록된다.
 * ===================================================================== */

import React, { useMemo } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import {
  resolveSpecializedAgents,
  resolveTenantDefaultPath,
  resolveTenantInviteRedirectPath,
  type TenantSpecializedAgent,
  useTenant,
} from '@auw/tenant';
import {
  WorkspaceAppRouter,
  type WorkspaceAuthExtraRouteEntry,
  type WorkspacePublicRouteEntry,
  type WorkspaceRouteEntry,
} from '@auw/workspace-app';
import { AppSkeleton } from '@auw/workspace-ui';
import { AuthCallbackPage } from '@/components/auth/AuthCallbackPage';
import { hasAuthToken } from '@auw/api-client';
import { getActiveAuthAccount } from '@auw/api-client';
import { lazyWithRetry } from '@auw/workspace-utils';
import { useFeatureFlags } from '@auw/feature-flags';
import i18n from '@auw/i18n';
import { AuthGuard } from './AuthGuard';
import { SEOHead } from './SEOHead';
import { ROUTE_ENTRIES } from './routeConfig';

const SKELETON_DELAY_MS = 500;

const DelayedAppSkeleton: React.FC = () => {
  const [show, setShow] = React.useState(false);
  const location = useLocation();
  const tenant = useTenant();
  const defaultPath = resolveTenantDefaultPath(tenant);
  const suppressSkeleton = location.pathname === '/auth/callback'
    || (location.pathname === defaultPath && (hasAuthToken() || !!getActiveAuthAccount()));
  React.useEffect(() => {
    if (suppressSkeleton) {
      setShow(false);
      return undefined;
    }
    const id = window.setTimeout(() => setShow(true), SKELETON_DELAY_MS);
    return () => window.clearTimeout(id);
  }, [suppressSkeleton]);
  if (suppressSkeleton) return null;
  if (!show) return null;
  return <AppSkeleton />;
};

/* ---- Lazy Load Components ---- */
const TermsPage = lazyWithRetry(() => import('@/components/legal/TermsPage').then(m => ({ default: m.TermsPage })));
const PrivacyPage = lazyWithRetry(() => import('@/components/legal/PrivacyPage').then(m => ({ default: m.PrivacyPage })));
const MarketingConsentPage = lazyWithRetry(() => import('@/components/legal/MarketingConsentPage').then(m => ({ default: m.MarketingConsentPage })));
const PricingPage = lazyWithRetry(() => import('@auw/workspace-ui').then(m => ({ default: m.PricingPage })));
const ContactSalesPage = lazyWithRetry(() => import('@auw/workspace-ui').then(m => ({ default: m.ContactSalesPage })));
const PaddleCheckoutPage = lazyWithRetry(() => import('@auw/workspace-ui').then(m => ({ default: m.PaddleCheckoutPage })));
const SharedWorkspacePage = lazyWithRetry(() => import('@auw/workspace-ui').then(m => ({ default: m.SharedWorkspacePage })));
const Layout = lazyWithRetry(() => import('@/components/layout/Layout').then(m => ({ default: m.Layout })));
const AgentLayout = lazyWithRetry(() => import('@auw/workspace-features').then(m => ({ default: m.AgentLayout })));
const LiveAgentLayout = lazyWithRetry(() => import('@auw/workspace-features').then(m => ({ default: m.LiveAgentLayout })));
const DocumentLayout = lazyWithRetry(() => import('@auw/workspace-features').then(m => ({ default: m.DocumentLayout })));
const DocAgentLandingLayout = lazyWithRetry(() => import('@auw/workspace-features').then(m => ({ default: m.DocAgentLandingLayout })));
const DocAgentEditorLayout = lazyWithRetry(() => import('@auw/workspace-features').then(m => ({ default: m.DocAgentEditorLayout })));
const StorageLayout = lazyWithRetry(() => import('@auw/workspace-features').then(m => ({ default: m.StorageLayout })));
const MessageLayout = lazyWithRetry(() => import('@auw/workspace-features').then(m => ({ default: m.MessageLayout })));
const WidgetLayout = lazyWithRetry(() => import('@auw/workspace-features').then(m => ({ default: m.WidgetLayout })));
const FeedLayout = lazyWithRetry(() => import('@auw/workspace-features').then(m => ({ default: m.FeedLayout })));
const ToolsLayout = lazyWithRetry(() => import('@auw/workspace-features').then(m => ({ default: m.ToolsLayout })));
const AppManageLayout = lazyWithRetry(() => import('@auw/workspace-features').then(m => ({ default: m.AppManageLayout })));
const AdminLayout = lazyWithRetry(() => import('@auw/workspace-features').then(m => ({ default: m.AdminLayout })));
const PluginLayout = lazyWithRetry(() => import('@auw/workspace-features').then(m => ({ default: m.PluginLayout })));
const SkillCreateLayout = lazyWithRetry(() => import('@auw/workspace-features').then(m => ({ default: m.SkillCreateLayout })));
const CADAgentPage = lazyWithRetry(() => import('@/features/specialized/cad/CADAgentPage'));
const CadFileDetailPage = lazyWithRetry(() => import('@/features/specialized/cad/CadFileDetailPage'));
const CadMaterialLandingPage = lazyWithRetry(
  () => import('@/features/specialized/cad/material/MaterialLandingPage'),
);
const CadMaterialProjectPage = lazyWithRetry(
  () => import('@/features/specialized/cad/material/MaterialProjectPage'),
);
const CadDrawingLandingPage = lazyWithRetry(
  () => import('@/features/specialized/cad/drawing/DrawingLandingPage'),
);
const CadDrawingProjectPage = lazyWithRetry(
  () => import('@/features/specialized/cad/drawing/DrawingProjectPage'),
);
const EstimateAgentPage = lazyWithRetry(
  () => import('@/features/specialized/estimate/EstimateAgentPage'),
);
const EstimateFileDetailPage = lazyWithRetry(
  () => import('@/features/specialized/estimate/EstimateFileDetailPage'),
);

/* ------------------------------------------------------------------ */
/* AuthenticatedLayout — Layout + SEOHead를 감싸는 래퍼                   */
/* ------------------------------------------------------------------ */

const AuthenticatedLayout: React.FC = () => (
  <>
    <SEOHead />
    <Layout />
  </>
);

const CADAgentLayout: React.FC = () => <CADAgentPage />;
const CADFileDetailLayout: React.FC = () => <CadFileDetailPage />;
const CADMaterialLandingLayout: React.FC = () => <CadMaterialLandingPage />;
const CADMaterialProjectLayout: React.FC = () => <CadMaterialProjectPage />;
const CADDrawingLandingLayout: React.FC = () => <CadDrawingLandingPage />;
const CADDrawingProjectLayout: React.FC = () => <CadDrawingProjectPage />;
const EstimateAgentLayout: React.FC = () => <EstimateAgentPage />;
const EstimateFileDetailLayout: React.FC = () => <EstimateFileDetailPage />;

type SpecializedRouteFactory = (
  agent: TenantSpecializedAgent,
) => readonly WorkspaceAuthExtraRouteEntry[];

const trimTrailingSlash = (path: string): string => {
  if (path === '/') return path;
  return path.replace(/\/+$/, '');
};

const SPECIALIZED_AGENT_ROUTE_REGISTRY: Record<string, SpecializedRouteFactory> = {
  cad: (agent) => {
    const basePath = trimTrailingSlash(agent.path);
    return [
      {
        path: basePath,
        element: <CADAgentLayout />,
      },
      {
        path: `${basePath}/file/:fileId`,
        element: <CADFileDetailLayout />,
      },
      {
        // CAD 자재관리 (엑셀 + 멀티턴 채팅) — CAD 에이전트의 하위 기능.
        path: `${basePath}/material`,
        element: <CADMaterialLandingLayout />,
      },
      {
        path: `${basePath}/material/:projectId`,
        element: <CADMaterialProjectLayout />,
      },
      {
        // CAD 도면 만들기 (text → CadQuery → STEP/STL 렌더링).
        path: `${basePath}/drawing`,
        element: <CADDrawingLandingLayout />,
      },
      {
        path: `${basePath}/drawing/:projectId`,
        element: <CADDrawingProjectLayout />,
      },
    ];
  },
  estimate: (agent) => {
    const basePath = trimTrailingSlash(agent.path);
    return [
      {
        path: basePath,
        element: <EstimateAgentLayout />,
      },
      {
        path: `${basePath}/file/:fileId`,
        element: <EstimateFileDetailLayout />,
      },
    ];
  },
};

const resolveWorkspaceRouteElement = (
  mode: (typeof ROUTE_ENTRIES)[number]['mode'],
  variant: 'base' | 'detail',
): React.ReactNode => {
  switch (mode) {
    case 'agent':
      return <AgentLayout />;
    case 'live-agent':
      return <LiveAgentLayout />;
    case 'document':
      return <DocumentLayout />;
    case 'doc-agent':
      return variant === 'detail' ? <DocAgentEditorLayout /> : <DocAgentLandingLayout />;
    case 'storage':
      return <StorageLayout />;
    case 'message':
      return <MessageLayout />;
    case 'widget':
      return <WidgetLayout />;
    case 'feed':
      return <FeedLayout />;
    case 'tools':
      return <ToolsLayout />;
    case 'app-manage':
      return <AppManageLayout />;
    case 'admin-page':
      return <AdminLayout />;
    case 'plugin':
      return <PluginLayout />;
    case 'skill-create':
      return <SkillCreateLayout />;
    default:
      return <AgentLayout />;
  }
};

/**
 * `/invite/team/:token` 에 이미 로그인 상태로 진입한 사용자를 테넌트가
 * 지정한 초대 랜딩 경로 (B2C: `/agents`) 로 보낸다. App.tsx 의 초대
 * 수락 effect 가 `?teamInvite=<token>` 파라미터를 읽어 실제 가입을
 * 처리한다.
 *
 * Phase 11-3a: 예전에는 `/agents` 를 코드에 박아놓았지만 이제는
 * `tenant.auth.inviteRedirectPath` (fallback = workspace.defaultPath)
 * 로 결정한다. 초대 UX 자체를 제공하지 않는 엔터프라이즈 테넌트는
 * 필드를 생략하면 기본 랜딩으로 떨어지므로 초대 링크가 "어디로도 가지
 * 않는 상태" 를 만들지 않는다.
 */
const InviteAuthedRedirect: React.FC = () => {
  const { token } = useParams<{ token?: string }>();
  const tenant = useTenant();
  const basePath = resolveTenantInviteRedirectPath(tenant);
  const nextPath = token
    ? `${basePath}?teamInvite=${encodeURIComponent(token)}`
    : basePath;
  return <Navigate to={nextPath} replace />;
};

// 레거시 결제 경로(/paddle-checkout)로 들어와도 _ptxn 쿼리를 유지해야
// Paddle checkout transaction link가 정상 동작합니다.
const LegacyCheckoutRedirect: React.FC = () => {
  const location = useLocation();
  return (
    <Navigate
      to={{
        pathname: '/checkout',
        search: location.search,
        hash: location.hash,
      }}
      replace
    />
  );
};

/* ------------------------------------------------------------------ */
/* Props                                                               */
/* ------------------------------------------------------------------ */

interface AppRouterProps {
  isAuthed: boolean;
  authChecking: boolean;
  inviteToken: string;
  onAuthed: () => Promise<void> | void;
}

/* ------------------------------------------------------------------ */
/* Router                                                              */
/* ------------------------------------------------------------------ */

export const AppRouter: React.FC<AppRouterProps> = ({
  isAuthed,
  authChecking,
  inviteToken,
  onAuthed,
}) => {
  const tenant = useTenant();
  /**
   * Post-login landing + `/` root redirect + 404 fallback target.
   * Centralised in the tenant manifest as of Phase 4c — B2C ships
   * `/widgets`, on-prem tenants can override with `workspace.defaultPath`.
   */
  const defaultPath = resolveTenantDefaultPath(tenant);

  /* P11-5b: tenant policy gates for the team-invite landing flow.
   * When `auth.teamInvite` is disabled we bypass the InviteHomePage
   * entirely and redirect to the configured invite landing target. */
  const teamInviteEnabled = useFeatureFlags().isEnabled('auth.teamInvite');
  const invitePath = teamInviteEnabled ? '/invite/team/:token' : undefined;

  const authGuardElement = (
    <AuthGuard
      isAuthed={isAuthed}
      authChecking={authChecking}
      inviteToken={inviteToken}
    />
  );

  /* routeConfig 의 `RouteEntry` 는 SEO 메타까지 포함하지만, shared
   * `WorkspaceAppRouter` 는 URL shape + route element 만 필요로 한다.
   * SEOHead 가 쿠션이 되어 SEO 메타는 인증 레이아웃 안에서 계속 렌더된다. */
  const authRoutes = useMemo<readonly WorkspaceRouteEntry[]>(
    () =>
      ROUTE_ENTRIES.map((entry) => ({
        path: entry.path,
        hasIdParam: entry.hasIdParam,
        element: resolveWorkspaceRouteElement(entry.mode, 'base'),
        idElement: resolveWorkspaceRouteElement(entry.mode, 'detail'),
      })),
    [],
  );

  const specializedRoutes = useMemo<readonly WorkspaceAuthExtraRouteEntry[]>(
    () =>
      resolveSpecializedAgents(tenant).flatMap((agent) => {
        const createRoutes = SPECIALIZED_AGENT_ROUTE_REGISTRY[agent.id];
        return createRoutes ? [...createRoutes(agent)] : [];
      }),
    [tenant],
  );

  /* 인증 레이아웃 안에서만 살아있는 보조 리다이렉트/전문 에이전트 라우트.
   * 전문 에이전트의 base path 는 매니페스트 `specializedAgents[].path` 를
   * 단일 source of truth 로 사용한다. */
  const authExtraRoutes = useMemo<readonly WorkspaceAuthExtraRouteEntry[]>(
    () => [
      {
        path: '/settings',
        element: <Navigate to="/settings/team" replace />,
      },
      ...specializedRoutes,
    ],
    [specializedRoutes],
  );

  const publicRoutes = useMemo<readonly WorkspacePublicRouteEntry[]>(
    () => [
      {
        path: '/auth/callback',
        element: (
          <>
            <SEOHead title={i18n.t('auth:callback.processing')} />
            <AuthCallbackPage onAuthed={onAuthed} />
          </>
        ),
      },
      { path: '/share/:token', element: <SharedWorkspacePage /> },
      {
        path: '/terms',
        element: (
          <>
            <SEOHead />
            <TermsPage />
          </>
        ),
      },
      {
        path: '/privacy',
        element: (
          <>
            <SEOHead />
            <PrivacyPage />
          </>
        ),
      },
      {
        path: '/marketing-consent',
        element: (
          <>
            <SEOHead />
            <MarketingConsentPage />
          </>
        ),
      },
      {
        path: '/pricing',
        element: (
          <>
            <SEOHead />
            <PricingPage />
          </>
        ),
      },
      {
        path: '/checkout',
        element: (
          <>
            <SEOHead />
            <PaddleCheckoutPage />
          </>
        ),
      },
      { path: '/paddle-checkout', element: <LegacyCheckoutRedirect /> },
      {
        path: '/contact-sales',
        element: (
          <>
            <SEOHead />
            <ContactSalesPage />
          </>
        ),
      },
    ],
    [onAuthed],
  );

  return (
    <WorkspaceAppRouter
      publicRoutes={publicRoutes}
      authGuardElement={authGuardElement}
      authenticatedLayoutElement={<AuthenticatedLayout />}
      authRoutes={authRoutes}
      authExtraRoutes={authExtraRoutes}
      defaultPath={defaultPath}
      invitePath={invitePath}
      inviteElement={teamInviteEnabled ? <InviteAuthedRedirect /> : null}
      suspenseFallback={<DelayedAppSkeleton />}
    />
  );
};
